/**
 * Printer Utilities
 * Unterstützung für Bluetooth Thermo-Drucker (58mm)
 * iPad: Verwendet AirPrint über native Druck-API
 */

import type { SalesInvoice, CartItem } from '../types';
import { formatCurrency } from './format';
import { LOGO_DATA, LOGO_BYTES_PER_ROW, LOGO_HEIGHT, LOGO_WIDTH } from './logoData';

export function generateESCPOSReceipt(invoice: SalesInvoice): Uint8Array {
  const ESC = 0x1b;
  const GS = 0x1d;
  const LINE_WIDTH = 32; // typische Zeichenanzahl für 58mm
  const bytes: number[] = [];

  const appendBytes = (...values: number[]): void => {
    bytes.push(...values);
  };

  const appendBuffer = (buffer: number[] | Uint8Array): void => {
    bytes.push(...buffer);
  };

  const appendLF = (count = 1): void => {
    for (let i = 0; i < count; i++) {
      bytes.push(0x0a);
    }
  };

  const encodeText = (text = ''): number[] => {
    const sanitized = text.replace(/\u00a0/g, ' ');
    const buffer: number[] = [];
    for (const char of sanitized) {
      const codePoint = char.codePointAt(0) ?? 0x3f;
      if (codePoint <= 0x7f) {
        buffer.push(codePoint);
        continue;
      }

      switch (codePoint) {
        case 0x20ac: // €
          buffer.push(0x80);
          break;
        case 0x00a7: // §
          buffer.push(0xa7);
          break;
        default:
          if (codePoint <= 0xff) {
            buffer.push(codePoint);
          } else {
            buffer.push(0x3f); // '?'
          }
      }
    }
    return buffer;
  };

  const appendText = (text = '', newline = true): void => {
    if (text.length) {
      appendBuffer(encodeText(text));
    }
    if (newline) {
      appendLF();
    }
  };

  const setAlign = (mode: 'left' | 'center' | 'right'): void => {
    const value = mode === 'left' ? 0 : mode === 'center' ? 1 : 2;
    appendBytes(ESC, 0x61, value);
  };

  const setStyle = (options?: {
    bold?: boolean;
    doubleWidth?: boolean;
    doubleHeight?: boolean;
  }): void => {
    let n = 0x00;
    if (options?.bold) n |= 0x08;
    if (options?.doubleHeight) n |= 0x10;
    if (options?.doubleWidth) n |= 0x20;
    appendBytes(ESC, 0x21, n);
  };

  const lineSeparator = (): void => {
    appendText('-'.repeat(LINE_WIDTH));
  };

  const wrapText = (text: string, width = LINE_WIDTH): string[] => {
    const result: string[] = [];
    let current = '';
    for (const char of text) {
      const prospective = current + char;
      if (prospective.length > width) {
        result.push(current);
        current = char;
      } else {
        current = prospective;
      }
    }
    if (current) {
      result.push(current);
    }
    return result.length ? result : [''];
  };

  const twoColumns = (
    left: string,
    right: string,
    tl = true,
    tr = true,
    width = LINE_WIDTH
  ): string => {
    const leftText = tl ? left.trim() : left;
    const rightText = tr ? right.trim() : right;
    const space = Math.max(width - (leftText.length + rightText.length), 1);
    return `${leftText}${' '.repeat(space)}${rightText}`;
  };

  const printLogo = (): void => {
    if (!LOGO_DATA.length) {
      return;
    }
    appendBytes(
      GS,
      0x76,
      0x30,
      0x00,
      LOGO_BYTES_PER_ROW,
      0x00,
      LOGO_HEIGHT & 0xff,
      (LOGO_HEIGHT >> 8) & 0xff
    );
    appendBuffer(LOGO_DATA);
    appendLF(1);
  };

  // Initialisierung
  appendBytes(ESC, 0x40); // INIT
  appendBytes(ESC, 0x74, 16); // Codepage: 16 = Windows-1252 (Euro-Unterstützung)
  printLogo();
  setAlign('center');
  setStyle({ doubleWidth: true, doubleHeight: true });
  appendText('HarzLiebe');
  setStyle();
  appendText('Ulrike Kollegger');
  appendText('Flurgasse 2c');
  appendText('7202 Bad Sauerbrunn');
  appendLF();

  // Datum / Nummer
  setAlign('center');
  const timestamp = new Date().toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
  appendText(timestamp);
  appendText(`Nr: ${invoice.invoice_number || invoice.id?.substring(0, 8) || 'N/A'}`);
  lineSeparator();

  // Positionen
  setAlign('left');
  invoice.items.forEach((item: CartItem) => {
    const productName = item.product.name || 'Artikel';
    wrapText(productName).forEach((line) => appendText(line));
    const qtyPrice = `${item.quantity}x ${formatCurrency(item.price)}`;
    const total = formatCurrency(item.subtotal);
    appendText(twoColumns(qtyPrice, total, true));
  });

  lineSeparator();

  // Gesamtbetrag
  setStyle({ doubleWidth: true });
  appendText(twoColumns('Gesamt', formatCurrency(invoice.total)));
  setStyle();

  // Zahlungsart
  const paymentMethod = invoice.payments[0]?.type === 'cash' ? 'Bar' : 'Karte';
  appendText(`Zahlung: ${paymentMethod}`);
  appendLF();

  // Footer
  setAlign('center');
  appendText('Vielen Dank!');
  appendText('Gemäß § 6 Abs. 1 Z 27 UStG');
  appendText('Kein Ausweis von USt');
  appendLF(3);

  // Cut / Feed
  appendBytes(GS, 0x56, 0x00); // Teil-Cut bzw. Feed für PT-210

  return Uint8Array.from(bytes);
}

/**
 * Generiert HTML für Druck (optimiert für AirPrint auf iPad - 58mm)
 */
function generateReceiptHTMLForPrint(invoice: SalesInvoice): string {
  const items = invoice.items
    .map((item: CartItem) => {
      const itemName =
        item.product.name.length > 24
          ? item.product.name.substring(0, 21) + '...'
          : item.product.name;
      return `
    <tr>
      <td class="text-left">
        <span>${itemName}</span><br />
        <span style="white-space:pre;">  ${item.quantity}x  ${formatCurrency(item.price)}</span>
      </td>
      <td style="text-align: right; vertical-align: top;">${formatCurrency(item.subtotal)}</td>
    </tr>
  `;
    })
    .join('');

  const paymentMethod = invoice.payments[0]?.type === 'cash' ? 'Bar' : 'Karte';

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=58mm, initial-scale=1.0">
  <title>Beleg ${invoice.invoice_number || invoice.id}</title>
  <style>
    @media print {
      @page {
        size: 58mm auto;
        margin: 0;
      }
      body {
        margin: 0;
        padding: 5mm;
      }
    }
    body {
      font-family: 'Courier New', monospace;
      font-size: 11px;
      line-height: 1.3;
      max-width: 48mm;
      margin: 0 auto;
      padding: 5mm;
    }
    .header {
      text-align: center;
      border-bottom: 1px dashed #000;
      padding-bottom: 8px;
      margin-bottom: 8px;
    }
    .header h1 {
      margin: 0;
      font-size: 14px;
      font-weight: bold;
    }
    .info {
      margin: 6px 0;
      font-size: 9px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0;
    }
    table td {
      padding: 3px 0;
      font-size: 10px;
    }
    table th {
      font-size: 9px;
      font-weight: bold;
      padding: 2px 0;
      text-align: left;
    }
    table th.text-right {
      text-align: right;
    }
    .total {
      border-top: 1px dashed #000;
      padding-top: 8px;
      margin-top: 8px;
      text-align: right;
      font-weight: bold;
      font-size: 12px;
    }
    .footer {
      text-align: center;
      margin-top: 12px;
      padding-top: 8px;
      border-top: 1px dashed #000;
      font-size: 9px;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>HarzLiebe</h1>
    <div>
      <span>Ulrike Kollegger</span><br />
      <span>Flurgasse 2c</span><br />
      <span>7202 Bad Sauerbrunn</span><br />
    </div>
    <div class="info">
      ${new Date().toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })}
    </div>
    <div class="info">
      Nr: ${invoice.invoice_number || invoice.id?.substring(0, 8) || 'N/A'}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="text-left">Artikel</th>
        <th class="text-right">Betrag</th>
      </tr>
    </thead>
    <tbody>
      ${items}
    </tbody>
  </table>

  <div class="total">
    <div>Gesamt: ${formatCurrency(invoice.total)}</div>
    <div style="font-size: 10px; margin-top: 4px;">Zahlung: ${paymentMethod}</div>
  </div>

  <div class="footer">
    <div>Vielen Dank!</div>
    <div>Gemäß § 6 Abs. 1 Z 27 UStG</div>
    <div>Kein Ausweis von USt</div>
  </div>
</body>
</html>
  `;
}

/**
 * Druckt Beleg über Web Bluetooth API (falls verfügbar)
 * Für iPad: Verwendet native Druck-API mit AirPrint
 * Fallback: Öffnet Beleg in neuem Fenster
 */
export async function printToBluetoothPrinter(invoice: SalesInvoice): Promise<void> {
  // Prüfe ob Web Bluetooth API verfügbar ist (nicht auf iOS/iPad)
  const hasBluetooth = 'bluetooth' in navigator && navigator.bluetooth;

  if (!hasBluetooth) {
    // iPad/iOS: Verwende native Druck-API mit AirPrint
    console.log('Web Bluetooth nicht verfügbar - verwende native Druck-API für iPad');
    await printViaAirPrint(invoice);
    return;
  }

  try {
    // Desktop/Android: Versuche Web Bluetooth
    const device = await navigator.bluetooth!.requestDevice({
      filters: [
        { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, // Serial Port Profile
        { namePrefix: 'POS' },
        { namePrefix: 'Printer' },
        { namePrefix: 'Thermal' }
      ],
      optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
    });

    console.log('Verbinde mit Drucker:', device.name);

    // Verbinde mit GATT Server
    const server = await device.gatt?.connect();
    if (!server) {
      throw new Error('Konnte nicht mit Drucker verbinden');
    }

    // Suche nach Serial Port Service
    const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
    const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

    // Generiere ESC/POS Daten
    const data = generateESCPOSReceipt(invoice);

    // Sende Daten in kleinen Blöcken (Bluetooth-GATT Limit)
    const CHUNK_SIZE = 180; // sicherer Wert für PT-210
    for (let offset = 0; offset < data.length; offset += CHUNK_SIZE) {
      const chunk = data.slice(offset, offset + CHUNK_SIZE);
      await characteristic.writeValue(chunk);
      // Kurze Pause, damit der Drucker nachkommt
      await new Promise((resolve) => setTimeout(resolve, 10));
    }

    console.log('Beleg erfolgreich gedruckt');
  } catch (err) {
    console.error('Fehler beim Bluetooth-Druck:', err);
    // Fallback: Öffne Beleg in neuem Fenster
    await printViaAirPrint(invoice);
  }
}

/**
 * Generiert ESC/POS-Daten als Blob für Download oder direkten Versand
 */
export function generateESCPOSBlob(invoice: SalesInvoice): Blob {
  const data = generateESCPOSReceipt(invoice);
  // Konvertiere Uint8Array zu ArrayBuffer für Blob
  // Erstelle neuen ArrayBuffer mit korrekter Länge
  const buffer = new ArrayBuffer(data.length);
  const view = new Uint8Array(buffer);
  view.set(data);
  return new Blob([buffer], { type: 'application/octet-stream' });
}

/**
 * Bietet ESC/POS-Daten zum Download an (für Bridge-Apps)
 */
export function downloadESCPOSReceipt(invoice: SalesInvoice): void {
  const blob = generateESCPOSBlob(invoice);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `beleg_${invoice.invoice_number || invoice.id || Date.now()}.bin`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Versucht Druck über URL-Schemes (für kompatible Drucker-Apps)
 */
function tryPrintViaURLScheme(invoice: SalesInvoice): boolean {
  // Versuche verschiedene URL-Schemes für Drucker-Apps
  const schemes = [
    // PrintCentral
    `printcentral://print?data=`,
    // Print n Share
    `printnshare://print?data=`,
    // GOOJPRT App (falls vorhanden)
    `goojprt://print?data=`
  ];

  // Generiere ESC/POS Daten als Base64
  const data = generateESCPOSReceipt(invoice);
  const base64 = btoa(String.fromCharCode(...data));

  // Versuche URL-Schemes
  for (const scheme of schemes) {
    try {
      const url = `${scheme}${encodeURIComponent(base64)}`;
      window.location.href = url;
      return true;
    } catch (err) {
      console.log(`URL-Scheme ${scheme} nicht verfügbar`);
    }
  }

  return false;
}

/**
 * Generiert PDF aus HTML-Beleg
 * Öffnet Druck-Dialog, wo Benutzer "Als PDF speichern" wählen kann
 */
function generatePDFFromHTML(invoice: SalesInvoice): void {
  const html = generateReceiptHTMLForPrint(invoice);

  // Erstelle Blob URL für bessere Kompatibilität
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);

  // Erstelle ein neues Fenster mit dem HTML
  const printWindow = window.open(url, '_blank', 'width=300,height=600');
  if (!printWindow) {
    alert('Popup-Blocker verhindert PDF-Generierung. Bitte Popup-Blocker deaktivieren.');
    URL.revokeObjectURL(url);
    return;
  }

  // Warte bis Inhalt geladen ist, dann öffne Druck-Dialog
  printWindow.addEventListener('load', () => {
    URL.revokeObjectURL(url);
    setTimeout(() => {
      printWindow.focus();
      // Öffne Druck-Dialog - Benutzer kann "Als PDF speichern" wählen
      printWindow.print();
    }, 500);
  });
}

/**
 * Zeigt Dialog mit Optionen für Beleg-Download
 */
function showPT210PrintDialog(invoice: SalesInvoice): void {
  // Erstelle Dialog HTML
  const dialogHTML = `
    <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 20px;">
      <div style="background: white; border-radius: 12px; padding: 24px; max-width: 500px; width: 100%; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
        <h2 style="margin: 0 0 16px 0; font-size: 20px; font-weight: bold;">Beleg herunterladen</h2>
        <p style="margin: 0 0 20px 0; color: #666; font-size: 14px;">
          Wählen Sie ein Format für den Beleg:
        </p>
        <div style="display: flex; flex-direction: column; gap: 12px;">
          <button id="pdf-btn" style="padding: 12px 20px; background: #3b82f6; color: white; border: none; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer;">
            PDF herunterladen
          </button>
          <button id="html-btn" style="padding: 12px 20px; background: #10b981; color: white; border: none; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer;">
            Beleg anzeigen
          </button>
          <button id="cancel-btn" style="padding: 12px 20px; background: #e5e7eb; color: #374151; border: none; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer;">
            Abbrechen
          </button>
        </div>
        <div style="margin-top: 20px; padding: 12px; background: #f3f4f6; border-radius: 8px; font-size: 12px; color: #6b7280;">
          <strong>Hinweis:</strong> Beim PDF-Download können Sie im Druck-Dialog "Als PDF speichern" wählen.
        </div>
      </div>
    </div>
  `;

  // Erstelle temporäres Element
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = dialogHTML;
  document.body.appendChild(tempDiv);

  // Event Handler
  const pdfBtn = tempDiv.querySelector('#pdf-btn');
  const htmlBtn = tempDiv.querySelector('#html-btn');
  const cancelBtn = tempDiv.querySelector('#cancel-btn');

  const closeDialog = () => {
    document.body.removeChild(tempDiv);
  };

  pdfBtn?.addEventListener('click', () => {
    generatePDFFromHTML(invoice);
    closeDialog();
  });

  htmlBtn?.addEventListener('click', async () => {
    // Öffne normale Beleg-Anzeige
    const { printReceipt } = await import('./receipt');
    printReceipt(invoice);
    closeDialog();
  });

  cancelBtn?.addEventListener('click', closeDialog);

  // Schließe bei Klick außerhalb
  tempDiv.querySelector('div > div')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) {
      closeDialog();
    }
  });
}

/**
 * Druckt Beleg über AirPrint (iPad/iOS)
 * Zeigt Dialog mit PDF-Download Option
 */
async function printViaAirPrint(invoice: SalesInvoice): Promise<void> {
  // Zeige Dialog mit PDF-Download Option
  showPT210PrintDialog(invoice);
}
